import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("stl-cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("stl-cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant, qty = 1) => {
    const key = `${product.slug}:${variant.sku}`;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + qty, 20) } : i));
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          sku: variant.sku,
          name: product.name,
          variantName: variant.name,
          price: variant.price,
          image: variant.image || product.image,
          qty,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
    setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const setQty = (key, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );
  const clear = () => setItems([]);

  const { subtotal, count } = useMemo(
    () => ({
      subtotal: items.reduce((s, i) => s + i.price * i.qty, 0),
      count: items.reduce((s, i) => s + i.qty, 0),
    }),
    [items]
  );

  return (
    <CartContext.Provider value={{ items, open, setOpen, addItem, removeItem, setQty, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
